import {useFieldArray, useFormContext, useWatch} from "react-hook-form";
import {RHFAutocomplete, RHFCheckbox, RHFSelect} from "../../../components/hook-form";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader, Chip,
    CircularProgress,
    Divider, Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField, Typography
} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {useSnackbar} from "notistack";
import {discountsOptionLabel} from "../helper";
import Iconify from "../../../components/Iconify";
import {useSelector} from "react-redux";
import {useLocales} from "../../../locales";
import PassengerDetails from "./PassengerDetails";
import PrivilegeDialog from "../PrivilegeDialog";
import SeatsDialog from "../SeatsDialog";
import {DIRECTION_NAME} from "../../../constants";
import {getPrices} from "../../../api/getPrices";
import {getPassengerDiscountOptions} from "../utils/getPassengerDiscountOptions";
import {getIniLoggedUser} from "../../../helper/session";
import {passengerModel} from "../models/passenger";
import dayjs from "dayjs";

export default function Passenger({tripData, open}) {
    const {enqueueSnackbar} = useSnackbar();
    const {translate} = useLocales();
    const {loadingDiscounts} = useSelector(state => state.discounts);
    const [privilegeDialogProps, setPrivilegeDialogProps] = useState({
        open: false,
        passengerIndex: undefined,
        data: null
    })
    const [seatDialogProps, setSeatDialogProps] = useState({
        open: false,
        isReturn: false,
    });
    const [countMenuOpen, setCountMenuOpen] = useState(false);
    const passengersCountRef = useRef(null);
    const typedCountRef = useRef({text: "", timer: null});

    const {
        control,
        setValue,
        getValues
    } = useFormContext();

    const {fields, append, remove, update} = useFieldArray({
        control,
        name: "passengers",
    });

    const foundDiscounts = useWatch({
        control,
        name: "foundDiscounts"
    })

    const foundReturnDiscounts = useWatch({
        control,
        name: "foundReturnDiscounts"
    })

    const passengers = useWatch({
        control,
        name: "passengers"
    })

    const passengersCount = useWatch({
        control,
        name: "passengersCount"
    })

    const directionType = useWatch({
        control,
        name: "directionType"
    })

    const searchDate = useWatch({
        control,
        name: "dateFrom"
    })

    const passengerFullName = (index) => {
        const name = passengers[index]?.name
        const lastName = passengers[index]?.surname
        const clientIdentity = passengers[index]?.clientIdentity
        if (name && lastName && clientIdentity) return `${name} ${lastName} ${clientIdentity}`
        return `Putnik ${index + 1}`
    }
    useEffect(() => {
        fetchData(tripData);
    }, [open, tripData, directionType])

    const fetchData = (tripData) => {
        if (tripData?.operatorId) {
            getPrices({
                rateCardId: tripData.rateCardId,
                stationId1: tripData.depStationId,
                stationId2: tripData.arrStationId,
                tripId: tripData.id,
                journeyDate: dayjs(searchDate).format('YYYY-MM-DD'),
            }).then(({data}) => {
                setValue('foundDiscounts', data?.filter(d => d.applyDiscountOneWay && d.priceOneWay !== null));
                setValue('foundReturnDiscounts', data?.filter(d => d.applyDiscountReturnTicket && d.priceReturnTicket !== null));
            }).catch(reason => {
                console.error("getPrices", reason)
                enqueueSnackbar(translate("errorFetching"), {variant: "error"});
            });
        }
    }


    /** Prevoznik sme i preko broja slobodnih mesta na svom polasku, ostali su ograničeni na 10. */
    const maxPassengers = () => {
        const limit = tripData?.operatorId === getIniLoggedUser().id
            ? 50
            : (tripData?.freeSeats > 10 ? 10 : tripData?.freeSeats);
        return Number(limit) || 0;
    }

    const applyPassengersCount = (count) => {
        const passengersArr = [...(getValues("passengers") || [])];
        if (count < passengersArr.length) {
            passengersArr.length = count;
        } else {
            for (let i = passengersArr.length; i < count; i++) {
                passengersArr.push(passengerModel);
            }
        }

        setValue("passengers", passengersArr);
        setValue("passengersCount", count);
    }

    const handlePassengerNumberChange = (event) => {
        applyPassengersCount(Number(event.target.value));
    }


    const handleAddPassClick = () => {
        append(passengerModel);
        setValue("passengersCount", Number(passengersCount) + 1)
    }

    const handleRemovePassClick = (index) => {
        remove(index);
        setValue("passengersCount", Number(passengersCount) - 1)
    }

    const handlePassengerOppositeDirection = (event, index) => {
        const {checked} = event.target;
        const psgDetails = getValues(`passengers[${index}]`)
        update(index, {
            ...psgDetails,
            discountPCT: null,
            haveOppositeDirection: checked,
        })

    }

    const handleClosePrivilegeDialog = () => {
        update(privilegeDialogProps.passengerIndex, passengerModel)
        setPrivilegeDialogProps({
            open: false,
            passengerIndex: undefined,
            data: null
        })
    }

    const handleOpenSeatChooser = (isReturn) => {
        if (tripData.externalSale) {
            return;
        }
        setSeatDialogProps({
            open: true,
            isReturn: isReturn
        })
    }

    const handleSeatConfirm = (vehicle, isReturnTicketData) => {
        // Prolazi kroz sve putnike i pronalazi onog koji već ima ovo sedište i vozilo
        passengers.forEach((passenger, index) => {
            if (passenger.seat === vehicle.seat && passenger.vehicleId === vehicle.vehicleId) {
                // Ako nađemo putnika sa istim seatNumber i vehicleId, postavi mu seatNumber i vehicleId na null
                if (isReturnTicketData) {
                    setValue(`passengers.${index}.returnSeat`, '');
                    setValue(`passengers.${index}.vehicleReturnId`, '');
                } else {
                    setValue(`passengers.${index}.seat`, '');
                    setValue(`passengers.${index}.vehicleId`, '');
                }
            }
        });
        if (isReturnTicketData) {
            setValue(`passengers.${seatDialogProps.passIndex}.returnSeat`, vehicle.seat || "");
            setValue(`passengers.${seatDialogProps.passIndex}.vehicleReturnId`, vehicle.vehicleId || "");
        } else {
            setValue(`passengers.${seatDialogProps.passIndex}.seat`, vehicle.seat || "");
            setValue(`passengers.${seatDialogProps.passIndex}.vehicleId`, vehicle.vehicleId || "");
        }
    };

    const onSubmitPrivilege = () => {
        setPrivilegeDialogProps({
            open: false,
            passengerIndex: undefined,
            data: null
        })
    }

    useEffect(() => {
        const isTypingElement = (el) => {
            const tag = el?.tagName;
            return el?.isContentEditable || tag === 'INPUT';
        };

        const onKeyDown = (e) => {
            if (isTypingElement(e.target)) return;

            if (e.key === 'Insert') {
                e.preventDefault();

                const current = fields.length;

                if (tripData?.freeSeats === current || current >= maxPassengers()) {
                    enqueueSnackbar("Nema dostupnih mesta za dodavanje putnika.", { variant: "info" });
                    return;
                }

                handleAddPassClick();
                return;
            }

            if (!/^[0-9]$/.test(e.key)) return;
            // Ctrl+1 do Ctrl+9 su prečice za pretragu stanica
            if (e.ctrlKey || e.altKey || e.metaKey) return;
            // dok je padajuća lista otvorena, cifru obrađuje sam MUI
            if (countMenuOpen || getValues("isSelectOpen")) return;
            e.preventDefault();

            const typed = typedCountRef.current;
            clearTimeout(typed.timer);
            typed.text = (typed.text + e.key).slice(-2);
            // druga cifra se nadovezuje samo ako stigne brzo, inače počinje nov broj
            typed.timer = setTimeout(() => {
                typedCountRef.current.text = "";
            }, 800);

            const wanted = parseInt(typed.text, 10);
            if (!wanted) return;

            const max = maxPassengers();
            if (wanted > max) {
                enqueueSnackbar(`Najveći dozvoljeni broj putnika je ${max}.`, { variant: "info" });
            }

            const count = Math.min(wanted, max);
            if (count < 1 || count === fields.length) return;

            applyPassengersCount(count);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [fields.length, tripData?.freeSeats, tripData?.operatorId, countMenuOpen, enqueueSnackbar]);

    // kartica polaska je otvorena za samo jedan polazak, pa kursor odmah ide na broj putnika
    useEffect(() => {
        const timer = setTimeout(() => passengersCountRef.current?.focus(), 0);
        return () => clearTimeout(timer);
    }, []);


    return (
        <>
            <Stack direction="row" justifyContent="space-between" marginTop={2} marginBottom={3}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Button
                        variant="outlined"
                        disabled={tripData?.freeSeats === fields.length}
                        onClick={handleAddPassClick}>
                        Dodaj putnika
                    </Button>
                    {tripData?.alSeatNumbered && <Button
                        variant="outlined"
                        disabled={tripData.externalSale ||
                            !tripData?.alSeatNumbered}
                        onClick={() =>
                            handleOpenSeatChooser(directionType === DIRECTION_NAME[1] &&
                                getValues("returnTrip"))}>
                        Biranje sedišta
                    </Button>}
                </Stack>
                <RHFSelect
                    name={`passengersCount`}
                    label="Broj putnika"
                    InputLabelProps={{shrink: true}}
                    SelectProps={{
                        native: false,
                        sx: {textTransform: 'capitalize'},
                        inputRef: passengersCountRef,
                        onOpen: () => setCountMenuOpen(true),
                        onClose: () => setCountMenuOpen(false)
                    }}
                    onChange={handlePassengerNumberChange}
                    sx={{maxWidth: 200}}>
                    {Array.from({length: maxPassengers()}, (_, index) => index + 1).map((option) => (
                        <MenuItem
                            key={`seat-${option}`}
                            value={option}
                            sx={{
                                mx: 1,
                                my: 0.5,
                                borderRadius: 0.75,
                                typography: 'body2',
                                '&:first-of-type': {mt: 0},
                                '&:last-of-type': {mb: 0},
                            }}>
                            {option}
                        </MenuItem>))}
                </RHFSelect>

            </Stack>
            <Box
                gap={2}
                display={fields.length > 2 ? "grid" : "flex"}
                gridTemplateColumns={{
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(3, 1fr)",
                }}>
                {fields.map((field, index) =>
                    <Card
                        key={field.id}
                        sx={{
                            flex: 1
                        }}>
                        <Grid container alignItems="center" spacing={2} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                            {/* Levi deo: Ime putnika i izabrano sedište */}
                            <Grid item xs>
                                {/* Glavni Stack koji sve ređa u jedan red */}
                                <Stack direction="row" alignItems="center" spacing={4}>

                                    {/* Naslov ostaje isti */}
                                    <Typography variant="h6" noWrap>
                                        {passengerFullName(index) || `Putnik ${index}`}
                                    </Typography>

                                    {/* Stack sa sedištima, sada kao drugi element u redu */}
                                    {(field?.seat || field?.returnSeat) && (
                                        <Stack direction="row" alignItems="center" spacing={2}>

                                            {field?.seat && (
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        Sedište:
                                                    </Typography>
                                                    <Chip
                                                        label={field.seat}
                                                        color="primary"
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </Stack>
                                            )}

                                            {field?.returnSeat && (
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        Povratno:
                                                    </Typography>
                                                    <Chip
                                                        label={field.returnSeat}
                                                        color="primary"
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </Stack>
                                            )}

                                        </Stack>
                                    )}
                                </Stack>
                            </Grid>

                            {/* Desni deo: Dugme za uklanjanje */}
                            <Grid item xs="auto">
                                <IconButton
                                    disabled={fields.length === 1}
                                    onClick={() => handleRemovePassClick(index)}
                                    color="error"
                                    title="Ukloni putnika"
                                >
                                    <Iconify width={25} icon="mdi:trash-can-outline" />
                                </IconButton>
                            </Grid>
                        </Grid>
                        <CardContent>
                            <Stack gap={2}>
                                <Stack
                                    gap={3}
                                    flexDirection={fields.length > 2 ? "column" : "row"}>
                                    {/*{directionType === DIRECTION_NAME[0] && <RHFCheckbox*/}
                                    {/*    disabled={tripData.externalSale}*/}
                                    {/*    onChange={(event) => handleHavePrivilegeChange(event, index)}*/}
                                    {/*    label="Registrovan putnik sa povlasticom"*/}
                                    {/*    name={`passengers.${index}.havePrivilege`}*/}
                                    {/*/>}*/}
                                    {([DIRECTION_NAME[0], DIRECTION_NAME[1]]).includes(directionType) && passengersCount > 1 &&
                                        <RHFCheckbox
                                            disabled={tripData.externalSale || !tripData?.priceReturnTicket}
                                            onChange={(event) => handlePassengerOppositeDirection(event, index)}
                                            label={directionType === DIRECTION_NAME[0] ? "Povratna" : "Jedan smer"}
                                            name={`passengers.${index}.haveOppositeDirection`}
                                        />
                                    }

                                    {(tripData?.depCountryName === "Srbija" || tripData.arrCountryName === "Srbija") &&
                                        <RHFAutocomplete
                                            fullWidth
                                            disabled={tripData.externalSale}
                                            options={
                                                (getPassengerDiscountOptions(
                                                    passengers?.[index],
                                                    directionType,
                                                    foundReturnDiscounts ?? [],
                                                    foundDiscounts ?? []
                                                ) ?? [])
                                            }
                                            getOptionLabel={(option) => discountsOptionLabel(option)}
                                            onChange={(event, value) => {
                                                setValue(`passengers.${index}.discountPCT`, value);
                                                if (value?.documentValidation) {
                                                    setPrivilegeDialogProps({
                                                        open: true,
                                                        passengerIndex: index,
                                                        data: passengers[index]
                                                    })
                                                }
                                            }}
                                            isOptionEqualToValue={(option, value) => option?.ticketTypeId === value?.ticketTypeId}
                                            value={passengers[index]?.discountPCT || null}
                                            name={`passengers.${index}.discountPCT`}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    label="Izaberite povlasticu / popust"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        size: "small",
                                                        startAdornment: (
                                                            loadingDiscounts ? <CircularProgress size={16}/> :
                                                                <Iconify width={16} icon={'mdi:discount-outline'}/>
                                                        ),
                                                    }}
                                                />
                                            }
                                        />
                                    }
                                </Stack>
                                <PassengerDetails
                                    isFlix={tripData.externalSale}
                                    showRows={fields.length > 2}
                                    index={index}/>
                            </Stack>
                        </CardContent>
                    </Card>
                )}
            </Box>
            <Divider sx={{borderStyle: 'dashed'}}/>
            {privilegeDialogProps.open &&
                <PrivilegeDialog
                    sales={foundDiscounts}
                    open={privilegeDialogProps.open}
                    passengerData={privilegeDialogProps?.data}
                    onClose={handleClosePrivilegeDialog}
                    onSubmit={onSubmitPrivilege}
                    passengerIndex={privilegeDialogProps.passengerIndex}
                />
            }
            {seatDialogProps.open &&
                // TODO dodati tajmer da dohvati sva sedista kad istekne rezervacija
                <SeatsDialog
                    open={seatDialogProps.open}
                    isReturnSelectionOn={seatDialogProps.isReturn}
                    onClose={() => setSeatDialogProps({
                        open: false,
                        isReturn: false
                    })}
                    tripData={tripData}
                />}
        </>
    )
}
